import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { groupServices } from "../../../../lib/groupServices";
import { Service } from "../../../../types";
import { Input } from "../../../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { ServiceTableRow } from "./ServiceTableRow";
import { StackGroup } from "./StackGroup";

interface ServicesTableProps {
  services: Service[];
  onRefresh: () => void;
  onDeploymentStarted: () => void;
}

export function ServicesTable({
  services,
  onRefresh,
  onDeploymentStarted,
}: ServicesTableProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return services;
    return services.filter((service) => {
      const haystack = [
        service.name,
        service.type,
        service.framework,
        service.status,
        service.stackName,
        service.repository?.branch,
        service.port != null ? String(service.port) : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [services, query]);

  // Split filtered services into stacks and standalone.
  // Stacks are rendered first as grouped visual sections; standalones go into
  // the regular table below. Ordering within each group follows the original
  // array order (API-returned creation order).
  const { standalone, stacks } = useMemo(() => groupServices(filtered), [filtered]);

  const hasResults = Object.keys(stacks).length > 0 || standalone.length > 0;

  return (
    <div className="space-y-3">
      {/* Search input */}
      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Search services…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {!hasResults ? (
        <div className="rounded-md bg-card">
          <Table>
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="h-24 text-center text-muted-foreground"
                >
                  No services match &ldquo;{query}&rdquo;.
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      ) : (
        <>
          {/* Stack groups — rendered before standalone services */}
          {Object.entries(stacks).map(([stackId, { primary, dependencies }]) => (
            <StackGroup
              key={stackId}
              stackName={primary.stackName ?? stackId}
              primaryService={primary}
              dependencyServices={dependencies}
              onRefresh={onRefresh}
              onDeploymentStarted={onDeploymentStarted}
            />
          ))}

          {/* Standalone (non-stacked) services */}
          {standalone.length > 0 && (
            <div className="rounded-md bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Framework</TableHead>
                    <TableHead>Port</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {standalone.map((service) => (
                    <ServiceTableRow
                      key={service.id}
                      service={service}
                      onRefresh={onRefresh}
                      onDeploymentStarted={onDeploymentStarted}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
